import axios from "axios";
import { FULFILLED, PENDING, REJECTED } from "./action-type.util";

export interface IAffiliateImage {
  affiliateID: number;
  imageUrl?: string;
  formData?: FormData;
}

export const ACTION_TYPES = {
  FETCH_AFFILIATE_LOGO: "affiliateImages/FETCH_AFFILIATE_LOGO",
  UPLOAD_AFFILIATE_LOGO: "affiliateImages/UPLOAD_AFFILIATE_LOGO",
  DELETE_AFFILIATE_LOGO: "affiliateImages/DELETE_AFFILIATE_LOGO",
};

export interface AffiliateImageState {
  loading: boolean;
  images: Array<IAffiliateImage>;
  uploadError: null;
}

const initialState: AffiliateImageState = {
  loading: false,
  images: [],
  uploadError: null,
};

const upsert = (images: IAffiliateImage[], affiliateID: number, patch: Partial<IAffiliateImage>) => {
  const idx = images.findIndex(x => x.affiliateID === affiliateID);
  if (idx >= 0) images[idx] = { ...images[idx], ...patch, affiliateID };
  else images.push({ affiliateID, ...(patch as any) });
};

export default (state: AffiliateImageState = initialState, action): AffiliateImageState => {
  switch (action.type) {
    case PENDING(ACTION_TYPES.FETCH_AFFILIATE_LOGO):
    case PENDING(ACTION_TYPES.DELETE_AFFILIATE_LOGO):
    case PENDING(ACTION_TYPES.UPLOAD_AFFILIATE_LOGO):
      return { ...state, loading: true };

    case FULFILLED(ACTION_TYPES.FETCH_AFFILIATE_LOGO): {
      const { status, data } = action.payload as { status: number; data: Blob };
      const affiliateID = action.meta.affiliateID;

      if (status === 404) {
        return {
          ...state,
          loading: false,
          images: state.images.filter(x => x.affiliateID !== affiliateID),
        };
      }

      // make an object URL for the blob
      const objectUrl = URL.createObjectURL(data);
      const images = [...state.images];
      upsert(images, affiliateID, { imageUrl: objectUrl });
      return { ...state, loading: false, images };
    }

    case FULFILLED(ACTION_TYPES.UPLOAD_AFFILIATE_LOGO): {
      const { status, data } = action.payload as { status: number; data: Blob };
      const affiliateID = action.meta.affiliateID;

      if (status === 404) {
        return {
          ...state,
          loading: false,
          images: state.images.filter(x => x.affiliateID !== affiliateID),
        };
      }

      const objectUrl = URL.createObjectURL(data);
      const images = [...state.images];
      upsert(images, affiliateID, { imageUrl: objectUrl });
      return { ...state, loading: false, images };
    }

    case FULFILLED(ACTION_TYPES.DELETE_AFFILIATE_LOGO): {
      const affiliateID = action.meta?.affiliateID as number;
      return {
        ...state,
        loading: false,
        images: state.images.filter(x => x.affiliateID !== affiliateID),
      };
    }

    case REJECTED(ACTION_TYPES.FETCH_AFFILIATE_LOGO):
    case REJECTED(ACTION_TYPES.DELETE_AFFILIATE_LOGO):
    case REJECTED(ACTION_TYPES.UPLOAD_AFFILIATE_LOGO):
      return { ...state, loading: false };

    default:
      return state;
  }
};

// -------- ACTIONS --------
export const fetchAffiliateLogo = (affiliateID: number) => ({
  type: ACTION_TYPES.FETCH_AFFILIATE_LOGO,
  meta: { affiliateID },
  payload: axios.get(`affiliates/${affiliateID}/affLogo`, {
    responseType: "blob",
    validateStatus: s => s === 200 || s === 404,
  }),
});

export const uploadAffiliateLogo = (
  affiliateID: number,
  formData: FormData,
  onProgress?: (percent: number) => void,
) => ({
  type: ACTION_TYPES.UPLOAD_AFFILIATE_LOGO,
  meta: { affiliateID },
  payload: axios
    .post(`affiliates/${affiliateID}/uploadAffiliateLogo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      validateStatus: s => s >= 200 && s < 300,
      onUploadProgress: e => {
        if (onProgress && e.total) {
          const percent = Math.round((e.loaded * 100) / e.total);
          onProgress(percent);
        }
      },
    })
    .then(() =>
      axios.get(`affiliates/${affiliateID}/affLogo`, {
        responseType: "blob",
        validateStatus: s => s === 200 || s === 404,
      }),
    ),
});

export const deleteAffiliateLogo = (affiliateID: number) => ({
  type: ACTION_TYPES.DELETE_AFFILIATE_LOGO,
  meta: { affiliateID },
  payload: axios.delete(`affiliates/${affiliateID}/deleteAffiliateLogo`),
});
